import { useState, useEffect } from 'react';
import { Zap } from '../../components/CustomIcons';

/* ============================================================
   AssistivOS Learner Prototype — mock data + persistent state
   ============================================================ */

export const STORAGE_KEY = 'aos_prototype_v1';

export const COLORS = {
  navy: '#0f2744',
  navyDeep: '#081827',
  orange: '#f97316',
  orangeDeep: '#c2410c',
  warmWhite: '#fafaf9',
  charcoal: '#1c1c1e',
  slate: '#64748b',
  success: '#15803d',
  surface: '#f1f5f9',
};

/* ---------------- MOCK DATA ---------------- */

export const MODULES = [
  {
    id: 'ai-foundations',
    title: 'AI Foundations',
    hours: 40,
    credential: 'CompTIA AI+',
    tagline: 'Learn industry fundamentals and earn an industry-recognized credential',
    field: 'Technology & IT',
    image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=1200&q=70&auto=format&fit=crop',
    color: '#0f2744',
  },
  {
    id: 'hvac',
    title: 'HVAC Fundamentals',
    hours: 50,
    credential: 'EPA 608 Certification',
    tagline: 'Master heating, cooling, and refrigeration systems',
    field: 'Skilled Trades',
    image: 'https://images.unsplash.com/photo-1621905251918-48416bd8575a?w=1200&q=70&auto=format&fit=crop',
    color: '#0f2744',
  },
  {
    id: 'medical',
    title: 'Medical Assistant Essentials',
    hours: 45,
    credential: 'NHA CCMA',
    tagline: 'Healthcare support career pathway',
    field: 'Healthcare',
    image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1200&q=70&auto=format&fit=crop',
    color: '#0f2744',
  },
];

export const LESSONS_BY_MODULE = {
  'ai-foundations': [
    {
      title: 'Lesson 1: Fundamentals & Career Pathways',
      image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=1000&q=70&auto=format&fit=crop',
      body: 'Artificial Intelligence is transforming every major industry, from logistics and finance to healthcare and public safety. Entry-level AI roles do not always require a four-year degree — many employers now prioritize verified skills, hands-on experience, and industry-recognized credentials like the CompTIA AI+. In this lesson you will map the modern AI career ladder, from data annotator and prompt engineer to junior ML operations roles. You will also see how transferable skills you already have — attention to detail, systems thinking, communication — translate directly into AI-adjacent work. By the end of the module, you will know exactly which entry point fits your background and how to reach it.',
      bullets: [
        'AI+ certified roles pay 22% more than non-credentialed peers (BLS 2024)',
        'Prompt engineering and data annotation are the fastest entry points',
        'North Texas alone lists 1,200+ open AI-adjacent roles this quarter',
      ],
      question: 'Which of the following is a key entry-level AI role?',
      options: ['Chief Data Scientist', 'Data Annotator / Prompt Engineer', 'Compiler Architect', 'Cybersecurity Auditor'],
      correctIndex: 1,
    },
    {
      title: 'Lesson 2: How Language Models Work',
      image: 'https://images.unsplash.com/photo-1531297484001-80022131f5a1?w=1000&q=70&auto=format&fit=crop',
      body: 'Large Language Models (LLMs) are trained on massive text datasets to predict the next word in a sequence — but that simple mechanism unlocks reasoning, summarization, and code generation. Understanding tokens, context windows, and temperature helps you write prompts that produce consistent, useful output. In real workplaces, employees who can steer an LLM effectively save 5–10 hours per week on documentation, drafting, and customer response tasks. You do not need to build a model to be valuable — you need to know how to use one well.',
      bullets: [
        'Tokens are the smallest units an LLM reads — roughly 4 characters each',
        'Context windows define how much the model can "remember" in one turn',
        'Clear, structured prompts outperform clever prompts every time',
      ],
      question: 'What is a "token" in a language model?',
      options: ['A password used to log in', 'A billing unit only', 'The smallest text unit the model reads (~4 characters)', 'A hardware chip'],
      correctIndex: 2,
    },
    {
      title: 'Lesson 3: Responsible AI in the Workplace',
      image: 'https://images.unsplash.com/photo-1573164713988-8665fc963095?w=1000&q=70&auto=format&fit=crop',
      body: 'Every AI system carries risk: biased training data, hallucinated facts, and privacy exposure. Responsible AI practitioners verify outputs, cite sources, and never paste sensitive information into public tools. Employers increasingly require this literacy as a hiring baseline — the CompTIA AI+ exam dedicates an entire domain to it. Treat AI as a capable but unverified assistant: useful, fast, and always in need of a human check before its output leaves your desk.',
      bullets: [
        'Never paste PII, medical, or legal data into a public AI tool',
        'Always fact-check numeric claims and citations',
        'Log which tool produced which output for auditability',
      ],
      question: 'Which action reflects Responsible AI use at work?',
      options: ['Paste customer records into a public chatbot', 'Trust every AI answer without checking', 'Verify outputs and never share sensitive data with public tools', 'Hide which AI tool you used'],
      correctIndex: 2,
    },
  ],
  hvac: [
    {
      title: 'Lesson 1: Refrigeration Cycle Fundamentals',
      image: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=1000&q=70&auto=format&fit=crop',
      body: 'Every HVAC system — from a home window unit to a commercial rooftop chiller — runs on the same four-stage refrigeration cycle: compression, condensation, expansion, and evaporation. Mastering this cycle is the single most important step in becoming an HVAC technician. When a system fails, 80% of the time the root cause is a disruption in one of these four stages. In this lesson you will trace refrigerant through the loop, identify the pressures and temperatures at each stage, and learn why the EPA 608 certification is legally required to handle refrigerants on the job.',
      bullets: [
        'The four stages: Compress → Condense → Expand → Evaporate',
        'EPA 608 is federally required to buy and handle refrigerants',
        'North Texas HVAC techs average $52K after their first year',
      ],
      question: 'Which is the correct order of the refrigeration cycle?',
      options: ['Evaporate → Condense → Compress → Expand', 'Compress → Condense → Expand → Evaporate', 'Expand → Compress → Evaporate → Condense', 'Condense → Evaporate → Compress → Expand'],
      correctIndex: 1,
    },
    {
      title: 'Lesson 2: Reading Wiring Diagrams',
      image: null,
      icon: Zap,
      body: 'A wiring diagram is the map that lets you troubleshoot a unit in minutes instead of hours. Every HVAC diagram uses the same symbol library — contactors, capacitors, transformers, thermostats — and once you learn to read them, you can service equipment from any manufacturer. Field technicians who can trace a diagram confidently earn service calls that pay 30–40% more than install-only techs. This lesson introduces the standard symbols, low-voltage vs high-voltage circuits, and the safe sequence for testing components with a multimeter.',
      bullets: [
        'Low-voltage (24V) and line-voltage (240V) circuits must never be confused',
        'A multimeter is the technician\'s single most-used tool',
        'Diagram literacy is what separates installers from service techs',
      ],
      question: 'What voltage is a standard HVAC control (thermostat) circuit?',
      options: ['12V DC', '24V AC', '120V AC', '240V AC'],
      correctIndex: 1,
    },
    {
      title: 'Lesson 3: Safe Refrigerant Recovery',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1000&q=70&auto=format&fit=crop',
      body: 'Venting refrigerant into the atmosphere is a federal crime under the Clean Air Act. Every EPA 608–certified technician must recover refrigerant into an approved cylinder before opening a system for repair or disposal. This lesson walks through the recovery machine setup, the difference between passive and active recovery, and how to properly weigh and label a cylinder for reclaim. Employers verify these skills on Day 1 — get them right and you are trusted with high-value service calls.',
      bullets: [
        'Refrigerant venting can carry fines of $10,000+ per incident',
        'Always weigh cylinders before and after recovery',
        'Never mix refrigerant types in the same recovery cylinder',
      ],
      question: 'Before opening a refrigerant line, a technician must:',
      options: ['Vent slowly to the atmosphere', 'Recover refrigerant into an approved cylinder', 'Purge with nitrogen only', 'Nothing — it evaporates safely'],
      correctIndex: 1,
    },
  ],
  medical: [
    {
      title: 'Lesson 1: The Medical Assistant\'s Role',
      image: 'https://images.unsplash.com/photo-1584982751601-97dcc096659c?w=1000&q=70&auto=format&fit=crop',
      body: 'Certified Clinical Medical Assistants (CCMAs) are the operational backbone of outpatient healthcare. They room patients, take vitals, document histories, administer injections, and hand off clinical information to providers. In a typical Texas clinic, a CCMA supports 2–3 providers and touches every patient who walks through the door. The NHA CCMA credential is portable across all 50 states and is the fastest route into clinical healthcare — most programs are completed in 4–6 months with entry wages between $32K and $38K.',
      bullets: [
        'NHA CCMA is recognized in all 50 states',
        'DFW clinics list 800+ open CCMA roles this quarter',
        'Bilingual (Spanish/English) CCMAs earn a 6–8% wage premium',
      ],
      question: 'The NHA CCMA credential is:',
      options: ['Only valid in Texas', 'Recognized in all 50 states', 'Only for hospital settings', 'A four-year degree'],
      correctIndex: 1,
    },
    {
      title: 'Lesson 2: Vital Signs & Documentation',
      image: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=1000&q=70&auto=format&fit=crop',
      body: 'Accurate vital signs — blood pressure, pulse, respiration, temperature, and SpO2 — drive every clinical decision that follows. A mis-recorded BP can lead to a missed hypertensive crisis; a mistyped medication list can trigger a dangerous interaction. This lesson teaches manual and automated BP technique, common pitfalls (wrong cuff size, patient positioning), and how to document cleanly in an Electronic Health Record (EHR). Documentation is legally admissible — if it is not written down, it did not happen.',
      bullets: [
        'A cuff too small can overstate BP by 10–15 mmHg',
        'Always document the arm, position, and cuff size used',
        'EHR entries are permanent legal records',
      ],
      question: 'Using a BP cuff that is too small will typically:',
      options: ['Show falsely low blood pressure', 'Have no effect', 'Show falsely high blood pressure', 'Only affect the pulse reading'],
      correctIndex: 2,
    },
    {
      title: 'Lesson 3: HIPAA & Patient Privacy',
      image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1000&q=70&auto=format&fit=crop',
      body: 'HIPAA is not paperwork — it is a federal law that protects every patient you will ever meet. Medical assistants are on the front line: at the check-in desk, on the phone, and in every conversation between rooms. A single accidental disclosure — even to a family member — can end a career and expose an employer to six-figure fines. This lesson covers the minimum-necessary rule, safe verbal handoffs, and how to respond when someone asks a question you should not answer. Employers screen for HIPAA fluency in every CCMA interview.',
      bullets: [
        'Share only the minimum information necessary for the task',
        'Never confirm a patient\'s presence to unverified callers',
        'A HIPAA breach can carry personal fines, not just employer fines',
      ],
      question: 'A friend calls asking if their cousin is a patient. You should:',
      options: ['Confirm and give the room number', 'Confirm but not share details', 'Neither confirm nor deny the person is a patient', 'Transfer them to the provider'],
      correctIndex: 2,
    },
  ],
};

export const EMPLOYERS_BY_MODULE = {
  'ai-foundations': [
    { id: 'techforce', name: 'TechForce Solutions', salary: '$52,000–$68,000', openings: 84, field: 'AI & Cloud Operations', location: 'Plano, TX', title: 'Junior AI Operations Analyst', responsibilities: ['Monitor production AI pipelines and flag anomalies', 'Assist with prompt engineering for internal tools', 'Document model behavior and edge cases', 'Support senior engineers on data quality reviews'], credential: 'CompTIA AI+' },
    { id: 'lonestar', name: 'Lone Star Data Partners', salary: '$48,000–$62,000', openings: 41, field: 'Data Annotation & QA', location: 'Dallas, TX', title: 'Data Annotation Specialist', responsibilities: ['Label training data for computer vision models', 'Run quality reviews on annotator output', 'Maintain labeling guidelines and edge-case docs', 'Collaborate with ML engineers on data gaps'], credential: 'CompTIA AI+' },
    { id: 'ntxcloud', name: 'North Texas Cloud Collective', salary: '$55,000–$72,000', openings: 63, field: 'Cloud & AI Support', location: 'Irving, TX', title: 'Cloud AI Support Technician', responsibilities: ['Triage customer AI integration tickets', 'Reproduce and document technical issues', 'Escalate to senior engineers with clear repro steps', 'Maintain internal AI knowledge base'], credential: 'AWS Certified AI Practitioner' },
    { id: 'urbanai', name: 'Urban AI Services', salary: '$50,000–$65,000', openings: 27, field: 'Applied AI', location: 'Fort Worth, TX', title: 'AI Implementation Associate', responsibilities: ['Deploy pre-built AI workflows for small business clients', 'Train client staff on AI tool usage', 'Track adoption and outcomes', 'Report to the AI Solutions Manager'], credential: 'CompTIA AI+' },
  ],
  hvac: [
    { id: 'techforce', name: 'TechForce Solutions', salary: '$45,000–$55,000', openings: 127, field: 'HVAC & Building Systems', location: 'Plano, TX', title: 'HVAC Technician — Entry Level', responsibilities: ['Install and service residential HVAC systems', 'Recover and recharge refrigerant per EPA 608 standards', 'Troubleshoot electrical and mechanical faults', 'Communicate service findings to customers'], credential: 'EPA 608 Certification' },
    { id: 'lonestar', name: 'Lone Star Energy Partners', salary: '$52,000–$68,000', openings: 43, field: 'Commercial HVAC & Renewable Energy', location: 'Denton, TX', title: 'Commercial HVAC Apprentice', responsibilities: ['Support senior techs on rooftop unit service', 'Perform preventive maintenance on chillers', 'Document service tickets in the field CRM', 'Assist with heat pump retrofits'], credential: 'EPA 608 Certification' },
    { id: 'urban', name: 'Urban Plumbing & Mechanical', salary: '$38,000–$52,000', openings: 156, field: 'Plumbing & Mechanical', location: 'Arlington, TX', title: 'Mechanical Service Helper', responsibilities: ['Assist licensed techs on service calls', 'Load and stock service vehicles', 'Perform basic diagnostics under supervision', 'Learn code and inspection procedures'], credential: 'EPA 608 Certification' },
    { id: 'ntxair', name: 'North Texas Air Systems', salary: '$46,000–$58,000', openings: 62, field: 'Residential HVAC', location: 'Frisco, TX', title: 'Residential Service Tech I', responsibilities: ['Diagnose and repair residential HVAC failures', 'Perform seasonal tune-ups', 'Recommend and quote replacement equipment', 'Maintain a company service vehicle'], credential: 'EPA 608 Certification' },
  ],
  medical: [
    { id: 'dfwmed', name: 'DFW Medical Services', salary: '$32,000–$38,000', openings: 89, field: 'Healthcare Support', location: 'Dallas, TX', title: 'Certified Clinical Medical Assistant', responsibilities: ['Room patients and record vital signs', 'Document histories in the EHR', 'Administer injections under provider supervision', 'Manage clinic flow and patient handoffs'], credential: 'NHA CCMA' },
    { id: 'plano-clinic', name: 'Plano Community Clinics', salary: '$34,000–$40,000', openings: 47, field: 'Primary Care', location: 'Plano, TX', title: 'Medical Assistant — Primary Care', responsibilities: ['Prepare exam rooms and instruments', 'Assist providers during patient exams', 'Collect specimens for lab processing', 'Educate patients on care plans'], credential: 'NHA CCMA' },
    { id: 'ntx-health', name: 'North Texas Health Network', salary: '$33,000–$41,000', openings: 68, field: 'Multi-specialty', location: 'Fort Worth, TX', title: 'Clinical Medical Assistant', responsibilities: ['Support 2–3 providers across specialties', 'Perform EKGs and phlebotomy', 'Handle prior authorizations', 'Maintain HIPAA compliance'], credential: 'NHA CCMA' },
    { id: 'urban-med', name: 'Urban Family Medicine', salary: '$32,500–$39,000', openings: 22, field: 'Family Practice', location: 'Irving, TX', title: 'Family Practice MA', responsibilities: ['Room and screen patients', 'Administer vaccines and injections', 'Manage referrals and follow-ups', 'Support quality reporting'], credential: 'NHA CCMA' },
  ],
};

/* ---------------- STATE ---------------- */

export const DEFAULT_STATE = {
  step: '1A',
  intake: { education: '', career: '', enrolled: '' },
  moduleId: null,
  employerId: null,
  currentLessonIdx: 0,
  completedLessons: {},
  hoursByModule: {},
  answerFeedback: null,
  savedEmployers: [],
};

export function useProtoState() {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return { ...DEFAULT_STATE, ...JSON.parse(raw) };
    } catch (_e) { /* ignore */ }
    return DEFAULT_STATE;
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (_e) { /* ignore */ }
  }, [state]);

  const update = (patch) => setState((s) => ({ ...s, ...(typeof patch === 'function' ? patch(s) : patch) }));

  const reset = () => {
    localStorage.removeItem(STORAGE_KEY);
    setState(DEFAULT_STATE);
  };

  return { state, update, reset };
}
